import { useSyncExternalStore } from 'react';
import { BACKEND_ENVIRONMENT } from '@/infrastructure/clients/backendEnv';
import { SET_TOKENS, STARTS_AS, cssVariable, tokenSpec } from './tokens';
import { readColour, rgbToHex, themeValues, tokenColour, writeColour } from './colour';
import { laidOver, viewportOf, type ViewportColors } from './viewport';
import { DEFAULT_THEMES } from './defaultThemes';
import { fontStylesheets } from './fonts';
import { TEXTURE_TILE, textureImage, textureOf } from './texture';

export type ThemeMode = 'light' | 'dark';

/** A theme: a token set, a plain value for every token (tokens.ts), from a row of the themes table or Lureo's own Light and Dark. */
export type ThemeRow = {
  /** The row's id in the table; Lureo's Light and Dark, which live in the code only, have none and cannot be edited. */
  uniqueId?: string;
  key: string;
  label: string;
  /** Its blurb, which people read beside it on the Themes page; null for none. */
  description: string | null;
  /** What native controls and toasts follow, and the theme list's group. */
  mode: ThemeMode;
  /** 'washed' for the washed-print themes, whose mark (ThemeDot) has a ring of its ink. */
  family: string | null;
  /** False is a soft delete: the app leaves the row out everywhere, and only the API brings it back. */
  visible: boolean;
  sortOrder: number;
  tokens: Record<string, string>;
};

export type ThemeId = string;

export type { ViewportColors } from './viewport';

export const THEME_STORAGE_KEY = 'lureo.theme';
/** The applied theme's tokens, for index.html to paint them before the app loads. */
export const THEME_CACHE_KEY = 'lureo.theme.css.2';
/** The themes table's rows as last read, so a load has them before it reads the table again; a new key since the tokens'
 * names took the lureo- prefix, as rows cached under the old one name none of them. */
const ROWS_CACHE_KEY = 'lureo.theme-rows';
const OLD_ROWS_CACHE_KEY = 'lureo.themes';

// Every token a set gives a value is a CSS custom property, with its darkness from its mode
const CSS_TOKENS = SET_TOKENS.map(token => token.name);
const TEXTURE_PROPERTIES = ['--lureo-texture', '--lureo-texture-image-size'];

// A colour as bare OKLCH channels, as rows saved before every colour was a whole colour hold some
const CHANNELS = /^-?[\d.]+\s+-?[\d.]+\s+-?[\d.]+(\s*\/\s*[\d.]+)?$/;

/** A colour a row holds as a whole CSS colour: channels wrapped in oklch(), anything else kept as written (hex and rgb() are colours already). */
function wholeColour(value: string): string {
  const text = value.trim();
  if (CHANNELS.test(text)) return `oklch(${text})`;
  return text;
}

// A link to another token (var(--lureo-brand-ink)), as rows and browsers' cached rows saved by the older generator hold
const LINK = /^var\(--([\w-]+)\)$/;

/** A token's value with any link to another token followed to the value it ends at, so the set holds a copy. */
function followed(tokens: Record<string, string>, name: string): string | undefined {
  let value = tokens[name];
  for (let depth = 0; depth < 8 && value !== undefined; depth++) {
    const link = value.trim().match(LINK);
    if (!link || tokens[link[1]] === undefined) break;
    value = tokens[link[1]];
  }
  return value;
}

/** A set's tokens as the app uses them: only the tokens there are, links made copies, and every colour a whole CSS colour. */
function tidied(tokens: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    CSS_TOKENS.flatMap(name => {
      const value = followed(tokens, name);
      if (value === undefined) return [];
      return [[name, tokenSpec(name)?.kind === 'colour' ? wholeColour(value) : value]];
    }),
  );
}

/**
 * The value a row lacking a token copies: for a token in STARTS_AS the row's own value of the token it starts as; for a token
 * that is one of the row's colours made see-through (outline washes, the button ring), that colour faded as Lureo's are; else
 * Lureo Light's or Dark's.
 */
function copiedValue(tokens: Record<string, string>, fallback: Record<string, string>, name: string, depth = 0): string {
  if (name in tokens) return tokens[name];
  const source = STARTS_AS[name];
  if (source && depth < 8) return copiedValue(tokens, fallback, source, depth + 1);
  const derive = tokenSpec(name)?.derive;
  if (derive && 'alpha' in derive && !derive.alpha[2] && depth < 8) {
    const [from, opacity] = derive.alpha;
    const colour = readColour(copiedValue(tokens, fallback, from, depth + 1));
    if (colour) return writeColour({ rgb: colour.rgb, alpha: colour.alpha * opacity });
  }
  return fallback[name];
}

// Lureo's own Light and Dark, from the code: seed/themes.json holds a value for every token
const OWN_THEMES: ThemeRow[] = DEFAULT_THEMES;

/**
 * A table row as the app uses it, a token set with a plain value for every token. Its mode is the row's own, set by
 * hand on the Themes page. A row lacking a token copies a value: a token in STARTS_AS the set's own matching one
 * (its link colour its brand ink), any other Lureo Light's or Dark's.
 */
const complete = (row: ThemeRow): ThemeRow => {
  const fallback = DEFAULT_THEMES.find(theme => theme.mode === row.mode)!.tokens;
  const full = CSS_TOKENS.every(name => name in row.tokens) ? row.tokens : Object.fromEntries(CSS_TOKENS.map(name => [name, copiedValue(row.tokens, fallback, name)]));
  return { ...row, tokens: tidied(full) };
};

// Lureo's own Light and Dark live in the code only: a table row with one of their keys is ignored
const OWN_KEYS = new Set(OWN_THEMES.map(row => row.key));

// Themes are staging only for now: production has only Lureo's own Light and Dark, whatever this browser kept or is given
const TABLE_ROWS = BACKEND_ENVIRONMENT !== 'production';

/** Every theme: Lureo's Light and Dark from the code, then the table's others, each completed. A row with visible false is left out, as if deleted. */
const withOwn = (table: readonly ThemeRow[]): ThemeRow[] =>
  TABLE_ROWS ? [...OWN_THEMES, ...table.filter(row => !OWN_KEYS.has(row.key) && row.visible).map(complete)] : OWN_THEMES;

// A corner token's four corners from its value, as border-radius reads one to four lengths: top left, top right, bottom right, bottom left
const cornersOf = (value: string): [string, string, string, string] | null => {
  // Spaces inside a function (calc(1rem + 2px)) belong to it: only those outside brackets part the corners
  const parts: string[] = [];
  let depth = 0;
  let part = '';
  for (const char of value.trim()) {
    if (char === '(') depth++;
    if (char === ')') depth = Math.max(0, depth - 1);
    if (/\s/.test(char) && depth === 0) {
      if (part) parts.push(part);
      part = '';
    } else part += char;
  }
  if (part) parts.push(part);
  if (parts.length < 2 || parts.length > 4) return null;
  const [tl, tr = tl, br = tl, bl = tr] = parts;
  return [tl, tr, br, bl];
};
// The corner tokens, whose sides and single corners have their own classes
const CORNER_TOKENS = CSS_TOKENS.filter(name => tokenSpec(name)?.kind === 'length' && name.startsWith('lureo-radius'));
// Each corner of a corner token the theme sets per corner, for the corner-t-, corner-tl- and other side classes
const cornerDeclarations = (theme: ThemeRow): [string, string][] =>
  CORNER_TOKENS.flatMap(name => {
    const corners = cornersOf(theme.tokens[name] ?? '');
    return corners ? (['tl', 'tr', 'br', 'bl'] as const).map((corner, i) => [`${cssVariable(name)}-${corner}`, corners[i]] as [string, string]) : [];
  });

/** A theme's custom properties: every token in its set, each corner of a corner token it sets per corner, and --lureo-dark (1 in a dark theme, 0 in a light one). */
const declarationsOf = (theme: ThemeRow): [string, string][] => [...CSS_TOKENS.map(name => [cssVariable(name), theme.tokens[name]] as [string, string]), ...cornerDeclarations(theme), [cssVariable('lureo-dark'), theme.mode === 'dark' ? '1' : '0']];

function cachedRows(): ThemeRow[] | null {
  try {
    window.localStorage.removeItem(OLD_ROWS_CACHE_KEY);
    if (!TABLE_ROWS) {
      // On production a theme kept from before goes, and so does a pick of one that isn't Light or Dark
      window.localStorage.removeItem(ROWS_CACHE_KEY);
      const pick = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (pick !== null && !OWN_KEYS.has(pick)) window.localStorage.removeItem(THEME_STORAGE_KEY);
      return null;
    }
    const rows = JSON.parse(window.localStorage.getItem(ROWS_CACHE_KEY) ?? 'null');
    return Array.isArray(rows) && rows.length > 0 ? withOwn(rows) : null;
  } catch {
    return null;
  }
}

let rows: ThemeRow[] = (typeof window !== 'undefined' && cachedRows()) || OWN_THEMES;
// Goes up whenever the theme in use or any of its tokens changes
let version = 0;
const rowListeners = new Set<() => void>();
const listeners = new Set<() => void>();
const pickListeners = new Set<(id: ThemeId) => void>();

const find = (id: string | null) => rows.find(row => row.key === id);

/** The default for `mode`, which the light/dark toggle switches to: always Lureo's own Light or Dark, from the code. */
export const defaultTheme = (mode: ThemeMode): ThemeRow => OWN_THEMES.find(row => row.mode === mode)!;

// The theme this browser last picked, whether or not the rows have it yet
function picked(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

const stored = (): ThemeId => find(picked())?.key ?? defaultTheme('light').key;

let current: ThemeId = typeof window !== 'undefined' ? stored() : OWN_THEMES[0].key;

export const themeOf = (id: ThemeId): ThemeRow => find(id) ?? defaultTheme('light');

/** Lureo's own Light and Dark, the bundled defaults: fixed, so changing one starts from a duplicate. */
export const isFixedTheme = (theme: ThemeRow): boolean => OWN_KEYS.has(theme.key);

/** Whether a value names a theme there is (an account preference may name one that no longer exists). */
export const isThemeId = (id: unknown): id is ThemeId => typeof id === 'string' && find(id) !== undefined;

export const themeMode = (id: ThemeId): ThemeMode => themeOf(id).mode;

/** A theme's colour for a token, derived ones included, whether or not the theme is the one in use. */
const colourIn = ({ tokens, mode }: ThemeRow, name: string) => tokenColour(name, themeValues(tokens, mode));

/** A theme's colour for a token as #rrggbb, a see-through one laid over its surface. */
const hexIn = (theme: ThemeRow, name: string) => rgbToHex(laidOver(colourIn(theme, name), colourIn(theme, 'lureo-surface')?.rgb ?? [255, 255, 255]));

/** A theme's 3D view as Unity draws it, every colour solid (viewportOf). */
export const viewportColors = (id: ThemeId): ViewportColors => {
  const theme = themeOf(id);
  return viewportOf(theme.tokens, theme.mode);
};

/** A theme's mark: its page, its strongest ink and its brand colour. */
export const swatchOf = (theme: ThemeRow) => ({
  page: hexIn(theme, 'lureo-page'),
  ink: hexIn(theme, 'lureo-ink-strong'),
  brand: hexIn(theme, 'lureo-brand'),
});

export const getTheme = () => current;

/** Loads the faces a theme names, dropping the last theme's it no longer needs. */
function loadFonts(theme: ThemeRow) {
  const wanted = new Set(fontStylesheets(theme.tokens));
  for (const link of document.querySelectorAll<HTMLLinkElement>('link[data-theme-font]')) {
    if (wanted.has(link.href)) wanted.delete(link.href);
    else link.remove();
  }
  for (const href of wanted) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.themeFont = 'true';
    document.head.appendChild(link);
  }
}

// The latest texture asked for wins, made once changes settle
let textureRequest = 0;
let textureTimer: ReturnType<typeof setTimeout> | undefined;
const TEXTURE_SETTLE_MS = 150;

/** The theme's texture on the page background: its pattern at its strength, its tile shown at its size. */
function applyTexture({ tokens }: ThemeRow) {
  const root = document.documentElement.style;
  const request = ++textureRequest;
  const strength = Number(tokens['lureo-texture-strength']) || 0;
  if (tokens['lureo-texture-pattern'] === 'none' || strength <= 0) {
    TEXTURE_PROPERTIES.forEach(name => root.removeProperty(name));
    return;
  }
  const tile = parseFloat(tokens['lureo-texture-size'] ?? '') || TEXTURE_TILE;
  // Made once changes stop for a moment, so a slider's drag makes only the last
  clearTimeout(textureTimer);
  textureTimer = setTimeout(() => {
    if (request !== textureRequest) return;
    void textureImage(textureOf(tokens['lureo-texture-pattern']), strength, tile).then(texture => {
      if (request !== textureRequest || !texture) return;
      root.setProperty('--lureo-texture-image-size', texture.size);
      root.setProperty('--lureo-texture', `url(${texture.url})`);
    });
  }, TEXTURE_SETTLE_MS);
}

/** Puts the theme on <html>: every token, its stylesheets and texture, data-theme and data-mode naming it, and its mode for native controls. */
function applyTheme(id: ThemeId) {
  const theme = themeOf(id);
  const root = document.documentElement;
  root.dataset.theme = theme.key;
  root.dataset.mode = theme.mode;
  if (theme.tokens['lureo-logo-style'] === 'shape') root.dataset.logo = 'shape';
  else delete root.dataset.logo;
  // Titles take the set's fill only when it has one: with none they keep their own colour
  if (theme.tokens['lureo-title-fill'] && theme.tokens['lureo-title-fill'] !== 'none') root.dataset.titleFill = '';
  else delete root.dataset.titleFill;
  // Disabled buttons take the set's disabled fill only when it has one: with none they fade
  if (theme.tokens['lureo-disabled-fill'] && theme.tokens['lureo-disabled-fill'] !== 'none') root.dataset.disabledFill = '';
  else delete root.dataset.disabledFill;
  root.style.colorScheme = theme.mode;
  const declarations = declarationsOf(theme);
  // A corner the last theme set on its own goes, so a theme with one value per token reads its whole token
  for (const name of CORNER_TOKENS) for (const corner of ['tl', 'tr', 'br', 'bl']) root.style.removeProperty(`${cssVariable(name)}-${corner}`);
  for (const [property, value] of declarations) root.style.setProperty(property, value);
  loadFonts(theme);
  applyTexture(theme);
  try {
    const css = declarations.map(([property, value]) => `${property}:${value};`).join('');
    window.localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ id: theme.key, mode: theme.mode, logo: root.dataset.logo ?? '', titleFill: root.dataset.titleFill !== undefined, disabledFill: root.dataset.disabledFill !== undefined, css, fontImport: fontStylesheets(theme.tokens).join(' ') }));
  } catch {
    // Private mode: the next load paints the default until the app applies the theme
  }
}

function switchTo(id: ThemeId) {
  current = id;
  version += 1;
  applyTheme(id);
  listeners.forEach(listener => listener());
}

/** Saves the theme in this browser and switches every open view to it: a theme picked, as onThemePicked hears. */
export function setTheme(id: ThemeId) {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    // Private mode: the theme lasts until the page closes
  }
  switchTo(id);
  pickListeners.forEach(listener => listener(id));
}

/** Calls `listener` with each theme picked (setTheme), never for a theme applied again as its rows change; returns the unsubscribe. */
export function onThemePicked(listener: (id: ThemeId) => void): () => void {
  pickListeners.add(listener);
  return () => {
    pickListeners.delete(listener);
  };
}

/** The theme list's groups: Light then Dark, each with its default first and the rest in alphabetical order. Every list of themes, and [ and ], go by these. */
export function themeGroups(list: readonly ThemeRow[]) {
  const ordered = [...list].sort((a, b) => a.label.localeCompare(b.label));
  return (['light', 'dark'] as const).map(mode => {
    const first = defaultTheme(mode).key;
    const themes = ordered.filter(row => row.mode === mode);
    return { label: mode === 'light' ? 'Light' : 'Dark', themes: [...themes.filter(row => row.key === first), ...themes.filter(row => row.key !== first)] };
  });
}

/** Every theme as the app has it now. */
export const getThemeRows = (): readonly ThemeRow[] => rows;

// The rows are kept for the next load once they stop changing, as a drag on the Themes page changes them many times a second
const ROWS_CACHE_DELAY_MS = 1000;
let rowsCacheTimer: ReturnType<typeof setTimeout> | undefined;

function cacheRows() {
  clearTimeout(rowsCacheTimer);
  rowsCacheTimer = undefined;
  try {
    window.localStorage.setItem(ROWS_CACHE_KEY, JSON.stringify(rows));
  } catch {
    // Private mode: the next load starts from the defaults
  }
}

function cacheRowsSoon() {
  clearTimeout(rowsCacheTimer);
  rowsCacheTimer = setTimeout(cacheRows, ROWS_CACHE_DELAY_MS);
}

// A reload or a closed tab inside that second still keeps the latest rows
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const flushRows = () => rowsCacheTimer !== undefined && cacheRows();
  window.addEventListener('pagehide', flushRows);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flushRows());
}

/**
 * The themes table's rows, as read (or a theme being edited, shown before it saves): kept for the next load, and the
 * theme in use applied again when its tokens changed. The theme this browser picked comes back once the rows have it;
 * one that is gone falls back to the light default.
 */
export function setThemeRows(next: ThemeRow[]) {
  const before = find(current);
  rows = withOwn(next);
  cacheRowsSoon();
  rowListeners.forEach(listener => listener());
  const wanted = stored();
  if (wanted !== current) switchTo(wanted);
  else if (JSON.stringify(before) !== JSON.stringify(find(current))) switchTo(current);
}

/** Calls `listener` whenever the table's rows change; returns the unsubscribe. */
export function onThemeRowsChange(listener: () => void): () => void {
  rowListeners.add(listener);
  return () => {
    rowListeners.delete(listener);
  };
}

/** Every theme, re-rendering when the table's rows change. */
export const useThemeRows = (): readonly ThemeRow[] => useSyncExternalStore(onThemeRowsChange, () => rows);

/**
 * Puts the theme on the page (its stylesheets, texture and a fresh cache, whatever index.html painted from) and follows a
 * theme picked in another tab. The host calls it once, before it renders.
 */
export function startThemes() {
  applyTheme(current);
  window.addEventListener('storage', event => {
    if (event.key === THEME_STORAGE_KEY) switchTo(stored());
  });
}

/** Calls `listener` with the theme in use on every change to it or its tokens; returns the unsubscribe. */
export function onThemeChange(listener: (id: ThemeId) => void): () => void {
  const notify = () => listener(current);
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

const subscribe = (listener: () => void) => onThemeChange(listener);

export function useTheme(): ThemeId {
  return useSyncExternalStore(subscribe, getTheme);
}

/** A number that goes up with every change to the theme in use or its tokens. */
export const getThemeVersion = () => version;

/** Copies the theme on this page onto another document, such as the detached timeline's window. */
export function mirrorTheme(target: Document) {
  const source = document.documentElement;
  const root = target.documentElement;
  root.dataset.theme = source.dataset.theme;
  root.dataset.mode = source.dataset.mode;
  if (source.dataset.logo) root.dataset.logo = source.dataset.logo;
  else delete root.dataset.logo;
  if (source.dataset.titleFill !== undefined) root.dataset.titleFill = '';
  else delete root.dataset.titleFill;
  if (source.dataset.disabledFill !== undefined) root.dataset.disabledFill = '';
  else delete root.dataset.disabledFill;
  root.style.cssText = source.style.cssText;
  target.querySelectorAll('link[data-theme-font]').forEach(link => link.remove());
  document.querySelectorAll<HTMLLinkElement>('link[data-theme-font]').forEach(link => target.head.appendChild(link.cloneNode()));
}
